import React, { useEffect, useState, useMemo, useCallback } from 'react';

import { Typography, Box, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';

import { useAppSelector, useAppDispatch, usePrevious } from 'hooks';

import { antennasActions } from 'state/antennas';
import { satellitesActions } from 'state/satellites';
import { schedulesActions } from 'state/schedules';
import { orbitsActions } from 'state/orbits';

import VisualiseActivityController from 'controllers/VisualiseSatActivity';

import ListDetailsPage from 'components/ListDetailsPage';
import SatelliteOverview from 'components/satellites/Overview';
import SatelliteListItem from 'components/satellites/ListItem';
import MapView from 'components/map/MapView';
import FilterStatus from 'components/schedules/FilterStatus';
import ScheduleDetail from 'components/schedules/Detailed';
import CurrentPassPanel from 'components/satellites/CurrentPassPanel';
import ScheduleSummaryList from 'components/schedules/ScheduleSummaryList';

import { SatelliteManager, OrbitManager, ScheduleManager } from 'models';
import { MapControl } from 'controllers';

const GROUND_STATION_REFRESH_RATE = 1000 * 60 * 5; // seconds

interface PageProps {}

const SatelliteOperationsPage: React.FunctionComponent<PageProps> = () => {
    const dispatch = useAppDispatch();
    const theme = useTheme();

    const {
        auth,
        satellites,
        schedules: schedulesObject,
    } = useAppSelector((state) => state);

    const schedules = schedulesObject.list.object;

    const [tabValue, setTabValue] = useState('upcoming-detailed');

    const [nextSchedule, setNextSchedule] =
        useState<ScheduleManager.Schedule | null>(null);

    const [foundSatelliteId, setFoundSatelliteId] =
        useState<SatelliteManager.Satellite['id']>();

    const [selectedSatelliteId, setSelectedSatelliteId] =
        useState<SatelliteManager.Satellite['id']>();

    const prevSelectedSatelliteId = usePrevious(selectedSatelliteId);

    const selectedSatellite = useMemo(
        () => satellites.list.object?.find((a) => a.id === selectedSatelliteId),
        [satellites.list.object, selectedSatelliteId],
    );

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [mapDispatch, setMapDispatch] =
        useState<(control: MapControl) => void>();

    const [visualiser, setVisualiser] = useState<VisualiseActivityController>();

    const [isRefreshing, setisRefreshing] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const [orbits, setOrbits] = useState<OrbitManager.Orbit[]>();

    const getSchedules = (filters?: ScheduleManager.Filters) => {
        dispatch(
            schedulesActions.getScheduleList({
                orbit__satellite: selectedSatelliteId,
                limit: 10,
                after_stop_time: new Date(
                    Date.now() - 1000 * 60 * 15,
                ).toISOString(),
                ordering: 'start_time',
                status: statusFilter !== '' ? statusFilter : undefined,
                ...filters,
            }),
        );
    };

    const getNextOrCurrentSchedule = useCallback(() => {
        dispatch(
            schedulesActions.getDetailedSchedules({
                orbit__satellite: selectedSatelliteId,
                after_stop_time: new Date(
                    Date.now() - 1000 * 60 * 5,
                ).toISOString(),
                ordering: 'start_time',
                limit: 1,
            }),
        ).then((nextSchedules) => {
            if (nextSchedules.length > 0) setNextSchedule(nextSchedules[0]);
        });
    }, [selectedSatelliteId]);

    //This ensures that the getSchedules() function is called only once the statusFilter has changed. The statusFilter is updated by the child components onClick(onStatusChange) prop.
    useEffect(() => {
        getSchedules();
    }, [statusFilter]);

    const getAllOrbits = () => {
        if (selectedSatelliteId) {
            dispatch(orbitsActions.getOrbitList()).then((results) => {
                setOrbits(results);
            });
        }
    };

    const selectedOrbit = orbits?.find(
        (o) => o.satellite?.id === selectedSatelliteId,
    );

    const refreshSatellite = () => {
        if (!isRefreshing) {
            dispatch(satellitesActions.getSatelliteList()).then(() => {
                setisRefreshing(false);
                getSchedules();
                getNextOrCurrentSchedule();
                getAllOrbits();
            });
            setisRefreshing(true);
        }
    };

    const handleMapRegistration = (
        mapDispatcher: (control: MapControl) => void,
    ) => {
        setMapDispatch(mapDispatcher);

        const v = new VisualiseActivityController(
            selectedSatellite ? [selectedSatellite] : [],
            dispatch,
            0,
        );

        setVisualiser(v);

        mapDispatcher(v);
        console.log('Map Registration');
    };

    useEffect(() => {
        if (prevSelectedSatelliteId !== selectedSatelliteId) {
            setFoundSatelliteId(
                satellites.list.object?.find(
                    (a) => a.id === selectedSatelliteId,
                )?.id,
            );
        }
    }, [selectedSatelliteId, satellites.list]);

    useEffect(() => {
        if (selectedSatellite) {
            getSchedules();
            getNextOrCurrentSchedule();
            getAllOrbits();
            visualiser?.updateSatellites(
                selectedSatellite ? [selectedSatellite] : [],
                0,
            );
        } else {
            visualiser?.clear();
        }
    }, [foundSatelliteId]);

    useEffect(() => {
        let handler: NodeJS.Timeout | null = null;

        if (
            auth.tokens.status === 'finished' &&
            satellites.list.status === 'idle'
        ) {
            dispatch(satellitesActions.getSatelliteList()).then(() => {
                setisRefreshing(false);
            });
            dispatch(antennasActions.getAntennaList());
            setisRefreshing(true);

            handler = setInterval(() => {
                if (!isRefreshing) {
                    dispatch(satellitesActions.getSatelliteList()).then(() => {
                        setisRefreshing(false);
                    });
                    dispatch(antennasActions.getAntennaList());
                    setisRefreshing(true);
                }
            }, GROUND_STATION_REFRESH_RATE);
        }

        return () => {
            if (handler) clearInterval(handler);
        };
    }, [auth.tokens.status]);

    let latestTle: string;

    if (selectedOrbit?.tle?.name) {
        latestTle = (
            selectedOrbit?.tle?.name + selectedOrbit?.tle?.elememt_set_number
        ).replaceAll(' ', '');
    } else {
        latestTle = '';
    }

    let tleDetails: [string, Date];

    if (selectedOrbit?.tle?.uploaded) {
        tleDetails = [latestTle, selectedOrbit?.tle?.uploaded];
    } else {
        tleDetails = ['', new Date()];
    }

    const SchedulesDetails = (
        <Box
            sx={{
                my: 1,
                py: 1,
            }}
        >
            <Typography variant="h6" sx={{ mb: 1 }}>
                Recent and Upcoming Booking Details
            </Typography>
            <Box
                sx={{
                    paddingBottom: 1,
                }}
            >
                <FilterStatus
                    statusFilter={statusFilter}
                    onStatusChange={setStatusFilter}
                />
            </Box>
            <Box
                sx={{
                    my: 1,
                    py: 1,
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gap: 1,
                        [theme.breakpoints.down('xl')]: {
                            gridTemplateRows: 'auto auto',
                        },
                        [theme.breakpoints.up('xl')]: {
                            gridTemplateColumns: '1fr 1fr',
                        },
                    }}
                >
                    {schedules &&
                        schedules.length > 0 &&
                        schedules.map((s) => (
                            <ScheduleDetail
                                key={`${s.id}`}
                                schedule={s}
                                context="satellite"
                            />
                        ))}
                    {schedules && schedules.length === 0 && (
                        <Box>
                            <Typography
                                variant="h6"
                                align="center"
                                sx={{ m: 2 }}
                            >
                                No Schedule&apos;s assigned to this antenna.
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );

    return (
        <ListDetailsPage
            title="Satellite Details"
            breadcrumbs={[
                { text: 'Home', link: '/' },
                { text: 'Satellite Overview' },
            ]}
            onSearchChange={(text) => setSearchText(text)}
            list={satellites.list.object
                ?.filter((s) => {
                    if (!s.is_visible) return false;
                    if (!searchText) return true;

                    const search = searchText.toUpperCase();

                    return (
                        s.name.toUpperCase().includes(search) ||
                        s.norad_name.toUpperCase().includes(search) ||
                        s.catalogue_number
                            .toString()
                            .toUpperCase()
                            .includes(search)
                    );
                })
                .map((s) => (
                    <SatelliteListItem
                        key={s.id}
                        satellite={s}
                        onClick={() => {
                            setSelectedSatelliteId(s.id);
                        }}
                    />
                ))}
        >
            {selectedSatellite && (
                <>
                    <Box sx={{ height: 480, mb: 2 }}>
                        <MapView registerMapControl={handleMapRegistration} />
                    </Box>
                    <Divider sx={{ mt: 1 }} />
                    {tleDetails && (
                        <SatelliteOverview
                            satellite={selectedSatellite}
                            onRefreshClick={refreshSatellite}
                            isRefreshing={isRefreshing}
                            tleDetails={tleDetails}
                        />
                    )}

                    <Divider sx={{ mt: 1 }} />

                    <TabContext value={tabValue}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <TabList
                                onChange={(_, newValue) =>
                                    setTabValue(newValue)
                                }
                                aria-label="Satellite Tabs"
                                variant="scrollable"
                            >
                                <Tab
                                    label="Upcoming Booking Details"
                                    value="upcoming-detailed"
                                />
                                <Tab label="Current Pass" value="current" />
                                <Tab
                                    label="All Upcoming Bookings"
                                    value="upcoming-all"
                                />
                                <Tab label="Past Bookings" value="historic" />
                            </TabList>
                        </Box>

                        <TabPanel value="upcoming-detailed">
                            {SchedulesDetails}
                        </TabPanel>
                        <TabPanel value="current">
                            {nextSchedule && (
                                <CurrentPassPanel schedule={nextSchedule} />
                            )}
                            {nextSchedule == null && (
                                <Typography variant="h6">
                                    No Next Schedule Loaded
                                </Typography>
                            )}
                        </TabPanel>
                        {/* 
                            orbit__satellite: selectedSatelliteId,
                            limit: 100,
                            after_stop_time: new Date(
                                Date.now() - 1000 * 60 * 15,
                            ).toISOString(),
                            ordering: 'start_time',
                        */}
                        <TabPanel value="upcoming-all">
                            <ScheduleSummaryList
                                context="satellite"
                                orbit__satellite={selectedSatelliteId}
                                ordering="start_time"
                                after_start_time={new Date().toISOString()}
                            />
                        </TabPanel>
                        <TabPanel value="historic">
                            <ScheduleSummaryList
                                context="satellite"
                                orbit__satellite={selectedSatelliteId}
                                ordering="-start_time"
                                before_start_time={new Date().toISOString()}
                            />
                        </TabPanel>
                    </TabContext>
                </>
            )}
            {!selectedSatellite && selectedSatelliteId == null && (
                <Typography variant="h6" align="center" sx={{ m: 2 }}>
                    Select A Satellite
                </Typography>
            )}
            {!selectedSatellite && selectedSatelliteId != null && (
                <Typography variant="h6" align="center" sx={{ m: 2 }}>
                    Cannot find Satellite with Id {selectedSatelliteId}
                </Typography>
            )}
        </ListDetailsPage>
    );
};

SatelliteOperationsPage.displayName = 'SatelliteOperationsPage';
export default SatelliteOperationsPage;
